import { UploadFileStateRecord, UploadFileStatus } from '../../../../../../shared/components/upload-files';
import { getOriginalBoxFileName } from '../../../../../client-requests/models';
import { CrlItemFile } from '../../../../models';
import { isCrlUploadForItem } from '../../../../shared/utils/crl-upload-scope.util';

/** A date-bucketed group of files, e.g. "Today · 2 files". */
export interface CrlFileDateGroup {
  /** Stable key for `@for` tracking (the yyyy-mm-dd day, or 'unknown'). */
  key: string;
  /** Human label: "Today", "Yesterday", or "Friday, Feb 23". */
  label: string;
  files: CrlItemFile[];
}

/**
 * An upload in progress (or just-completed), rendered at the foot of the "Today"
 * group in the Documents tab — the seat its finished file arrives in, so the row
 * is replaced in place rather than moving.
 *
 * `state` is a local union rather than the shared upload module's enum, keeping
 * the presentational tab free of that dependency.
 */
export interface CrlStagingRow {
  jobId: string;
  name: string;
  /** 'uploading' → progress bar + cancel X; 'done' → green check; 'error' → failed. */
  state: 'uploading' | 'done' | 'error';
  /** 0–100, for the progress bar + caption. */
  percent: number;
  bytesLoaded: number;
  bytesTotal: number;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Local midnight for a date, so day-bucketing ignores the time component. */
function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

/**
 * Groups item files by upload day, newest day first, and labels each bucket
 * relative to `now` ("Today" / "Yesterday") or as a weekday + short date
 * ("Friday, Feb 23"). Files without a parseable `createdAt` collapse into a
 * trailing "Earlier" bucket so they still render. Pure + static (no DI) per the
 * mapper convention; `now` is injected so the component/tests stay deterministic.
 */
export class CrlDocumentsTabMapper {
  static toDateGroups(files: CrlItemFile[], now: Date = new Date()): CrlFileDateGroup[] {
    if (files.length === 0) return [];

    const todayStart = startOfDay(now);
    const buckets = new Map<string, { sortKey: number; label: string; files: CrlItemFile[] }>();

    for (const file of files) {
      const parsed = file.createdAt ? new Date(file.createdAt) : null;
      const valid = parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;

      const key = valid ? new Date(startOfDay(valid)).toISOString().slice(0, 10) : 'unknown';
      const existing = buckets.get(key);
      if (existing) {
        existing.files.push(file);
        continue;
      }
      buckets.set(key, {
        // Unknown-date files sort last (oldest sortKey).
        sortKey: valid ? startOfDay(valid) : Number.NEGATIVE_INFINITY,
        label: valid ? this.labelForDay(startOfDay(valid), todayStart) : 'Earlier',
        files: [file],
      });
    }

    return [...buckets.values()]
      .sort((a, b) => b.sortKey - a.sortKey)
      .map((bucket) => ({
        // Keyed on the day rather than the index, so grouping the same files twice —
        // the capped list and the full one behind it — yields keys that line up.
        key: bucket.sortKey === Number.NEGATIVE_INFINITY ? 'unknown' : String(bucket.sortKey),
        label: bucket.label,
        // Server order, which is oldest first.
        files: bucket.files,
      }));
  }

  /**
   * The uploads bound to one request item, as presentational rows (progress %,
   * done/error state), in the order they were started.
   *
   * Rows are a projection of global upload state — the binding comes from each
   * record's own `entityData` — so progress reads the same whether the overlay
   * has been open since the drop or was reopened onto an upload already running.
   * A list-level bulk drop names no item and appears in none of these lists.
   *
   * `itemFiles` are the item's real, already-linked files. A record whose file is
   * among them has been handed off and its row is dropped, so the finished row
   * holds its seat until the real one takes it — never both, and never neither.
   */
  static toStagingRows(records: UploadFileStateRecord[], itemId: number, itemFiles: CrlItemFile[] = []): CrlStagingRow[] {
    const fileIds = new Set(itemFiles.map((file) => file.fileId));
    const fileNames = new Set(itemFiles.map((file) => file.name));

    return records
      .filter((record) => isCrlUploadForItem(record.entityData, itemId) && !this.hasLanded(record, fileIds, fileNames))
      .map((record) => ({
        jobId: record.jobId,
        // calculatedName carries the upload's timestamp suffix; strip it for display.
        name: getOriginalBoxFileName(record.calculatedName || record.data?.file?.name || 'File'),
        state: this.stagingState(record.status),
        percent: record.bytesTotal > 0 ? Math.round((record.bytesLoaded / record.bytesTotal) * 100) : 0,
        bytesLoaded: record.bytesLoaded,
        bytesTotal: record.bytesTotal,
      }));
  }

  /**
   * Whether the item's file list already carries this upload's file — the upload
   * finished AND the reload that surfaces it has landed.
   *
   * The status check comes first so an in-flight upload is never matched away by
   * a file that merely shares its name. The id is authoritative where the
   * response carries one: re-uploading `w2.pdf` alongside an existing `w2.pdf`
   * would otherwise hand off against the wrong file. Name is the fallback for
   * responses with no id.
   */
  private static hasLanded(record: UploadFileStateRecord, fileIds: Set<number>, fileNames: Set<string>): boolean {
    if (record.status !== UploadFileStatus.LOADED) return false;

    const fileId = record.data?.fileId;
    if (fileId != null) return fileIds.has(fileId);

    return fileNames.has(getOriginalBoxFileName(record.calculatedName ?? ''));
  }

  private static stagingState(status: UploadFileStatus): CrlStagingRow['state'] {
    if (status === UploadFileStatus.LOADED) return 'done';
    if (status === UploadFileStatus.ERROR) return 'error';
    return 'uploading';
  }

  private static labelForDay(dayStart: number, todayStart: number): string {
    const diffDays = Math.round((todayStart - dayStart) / MS_PER_DAY);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return new Date(dayStart).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });
  }
}
